/**
 * CommandPalette — Recherche globale (⌘K / Ctrl+K).
 *
 * Indexe les collections déjà chargées (demandes, tâches, campagnes,
 * événements, clubs) et les filtre selon le périmètre de l'utilisateur :
 * admin (Mélissa + direction) voit tout ; sinon, ses clubs + le national.
 * Présente sur toutes les pages (montée dans AppShell). Un clic sur un
 * résultat navigue vers la page concernée.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useRequests, useTasks, useCampagnes, useCalendarEvents, useClubs,
} from "../hooks/useV1Data";
import { canViewRequest, isAdmin, scopeClubs } from "../lib/roles";
import {
  IconSearch, IconInbox, IconCheckSquare, IconMegaphone, IconCalendar, IconUsers, IconX,
} from "../icons";

const GROUP_ICON = {
  demande: IconInbox,
  tache: IconCheckSquare,
  campagne: IconMegaphone,
  evenement: IconCalendar,
  club: IconUsers,
};
const GROUP_LABEL = {
  demande: "Demandes",
  tache: "Tâches",
  campagne: "Campagnes",
  evenement: "Événements",
  club: "Clubs",
};
const GROUP_PAGE = {
  demande: "demandes",
  tache: "projets",
  campagne: "campagnes",
  evenement: "calendrier",
  club: "demandes",
};
const GROUP_ORDER = ["demande", "tache", "campagne", "evenement", "club"];

function norm(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

export default function CommandPalette({ open, onClose, onNavigate, currentUser, appId }) {
  const { requests }  = useRequests();
  const { tasks }     = useTasks();
  const { campagnes } = useCampagnes();
  const { events }    = useCalendarEvents();
  const { clubs }     = useClubs();

  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const clubName = useMemo(() => {
    const map = {};
    (clubs || []).forEach((c) => { map[String(c.id)] = c.name || c.nom || String(c.id); });
    return (id) => map[String(id)] || null;
  }, [clubs]);

  // Index complet, filtré par périmètre club.
  const index = useMemo(() => {
    const admin = isAdmin(currentUser);
    const myClubs = new Set(scopeClubs(currentUser));
    const items = [];

    (requests || []).forEach((r) => {
      if (!r || !canViewRequest(currentUser, r)) return;
      items.push({
        type: "demande", id: r.id,
        title: r.subject || r.titre || r.title || r.objet || "Demande",
        sub: [clubName(r.club), r.status || r.statut].filter(Boolean).join(" · "),
      });
    });

    (tasks || []).forEach((t) => {
      if (!t || !canViewRequest(currentUser, t)) return;
      items.push({
        type: "tache", id: t.id,
        title: t.title || t.name || t.label || t.titre || "Tâche",
        sub: [clubName(t.club), t.status || t.statut].filter(Boolean).join(" · "),
      });
    });

    (campagnes || []).forEach((c) => {
      if (!c || !canViewRequest(currentUser, c)) return;
      items.push({
        type: "campagne", id: c.id,
        title: c.name || c.title || c.nom || "Campagne",
        sub: c.published ? "Publiée" : "Brouillon",
      });
    });

    (events || []).forEach((e) => {
      if (!e || !canViewRequest(currentUser, e)) return;
      items.push({
        type: "evenement", id: e.id,
        title: e.title || e.nom || e.name || "Événement",
        sub: clubName(e.club) || "Agenda",
      });
    });

    (clubs || []).forEach((c) => {
      if (!admin && !myClubs.has(String(c.id))) return;
      items.push({
        type: "club", id: c.id,
        title: c.name || c.nom || "Club",
        sub: "Club",
      });
    });

    return items;
  }, [requests, tasks, campagnes, events, clubs, currentUser, clubName]);

  const groups = useMemo(() => {
    const q = norm(query);
    const matched = q
      ? index.filter((it) => norm(it.title).includes(q) || norm(it.sub).includes(q))
      : index;
    const byType = {};
    matched.forEach((it) => {
      if (!byType[it.type]) byType[it.type] = [];
      if (byType[it.type].length < 6) byType[it.type].push(it);
    });
    return GROUP_ORDER.filter((g) => byType[g] && byType[g].length).map((g) => ({ type: g, items: byType[g] }));
  }, [index, query]);

  if (!open) return null;

  function pick(item) {
    onNavigate(GROUP_PAGE[item.type] || "dashboard");
    onClose();
  }

  return (
    <div className="v2-cmdk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="v2-cmdk" role="dialog" aria-modal="true" aria-label="Recherche globale">
        <div className="v2-cmdk__input-row">
          <IconSearch className="v2-cmdk__search-icon" />
          <input
            ref={inputRef}
            className="v2-cmdk__input"
            placeholder="Rechercher une demande, une tâche, un club, un événement…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Recherche"
          />
          <button className="v2-cmdk__close" onClick={onClose} aria-label="Fermer"><IconX size={16} /></button>
        </div>

        <div className="v2-cmdk__results">
          {groups.length === 0 ? (
            <div className="v2-cmdk__empty">Aucun résultat</div>
          ) : (
            groups.map((grp) => {
              const Icon = GROUP_ICON[grp.type];
              return (
                <div key={grp.type} className="v2-cmdk__group">
                  <div className="v2-cmdk__group-label">{GROUP_LABEL[grp.type]}</div>
                  {grp.items.map((it, i) => (
                    <button key={it.id || i} className="v2-cmdk__item" onClick={() => pick(it)}>
                      <span className="v2-cmdk__item-icon"><Icon size={16} /></span>
                      <span className="v2-cmdk__item-body">
                        <span className="v2-cmdk__item-title">{it.title}</span>
                        {it.sub && <span className="v2-cmdk__item-sub">{it.sub}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>

        <div className="v2-cmdk__footer">
          <span>↵ ouvrir</span>
          <span>Échap fermer</span>
          <span className="v2-cmdk__footer-scope">Limité à votre périmètre</span>
        </div>
      </div>
    </div>
  );
}
